import GenericButton from 'components/generic/GenericButton'
import {
  InputCNPJCPF,
  InputNumber,
  InputText
} from 'components/generic/GenericInputs'
import Modal from 'components/generic/Modal'
import { axiosAuth } from 'helpers/axiosInstances'
import { isUnauthorized } from 'helpers/errors'
import { CustomerModel } from 'interfaces/customer'
import { OperationModel } from 'interfaces/operation'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { clearLoading, setGlobalMessage, showLoading } from 'redux/actions'
import { GlobalState } from 'redux/types'
import styled from 'styled-components'
import { Customer } from './LinesModals'

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
`

const Button = styled(GenericButton)<{ isDeny?: boolean }>`
  margin: 0.3rem 0;
  width: 150px;
  color: ${(props) => props.theme.color.primary_main};
  background-color: ${(props) =>
    props.isDeny
      ? props.theme.color.button_deny
      : props.theme.color.secondary_main};

  &:hover {
    background-color: ${(props) =>
      props.isDeny
        ? props.theme.color.button_deny_hover
        : props.theme.color.secondary_light};
  }
`

const ItemRowTop = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  height: auto;
  padding: 1rem 0;
`

const CardGrid = styled.div`
  width: 100%;
  height: 100%;
  padding: 0.1rem;
`

const TableGrid = styled.div`
  background-color: ${(props) => props.theme.color.white1};
  border-radius: 15px;
  display: grid;
  grid-template-columns:
    minmax(100px, 150px) minmax(250px, 300px) minmax(200px, 250px)
    minmax(180px, 230px);
  grid-auto-rows: 35px;
  height: 100%;
  max-height: 700px;
  overflow: auto;
`

const GridTitle = styled.div<{ borderRadius?: string }>`
  display: flex;
  align-items: center;
  box-shadow: 1px 2px 4px gray;
  background: ${(props) => props.theme.color.primary_light};
  padding: 0.5rem 1rem;
  border-radius: ${(props) => props.borderRadius};
  position: sticky;
  top: 0;
  z-index: 10;
`

const Text = styled.p<{
  Color?: string
  fontSize?: string
  fontWeight?: string
  textAlign?: boolean
}>`
  font-size: ${(props) =>
    props.fontSize ? props.fontSize : props.theme.font.small};
  color: ${(props) => (props.Color ? props.Color : props.theme.color.white1)};
  font-weight: ${(props) => props.fontWeight};
  white-space: nowrap;
  text-align: ${(props) => (props.textAlign ? 'center' : 'none')};
`

interface Props {
  showModal: boolean
  handleModal: () => void
  data: OperationModel
  changeData: (value: OperationModel) => void
}

const FindCustomer = ({
  showModal,
  handleModal,
  data,
  changeData
}: Props): JSX.Element => {
  const user = useSelector((state: GlobalState) => state.user)
  const history = useHistory()
  const dispatch = useDispatch()
  const [customers, setCustomers] = useState<CustomerModel[]>([])
  const [values, setValues] = useState<CustomerModel>({
    id_syscambio: '',
    corporate_name: '',
    fantasy_name: '',
    document: ''
  })
  const [valuesFilter, setValuesFilter] = useState<CustomerModel>({
    id_syscambio: '',
    corporate_name: '',
    fantasy_name: '',
    document: ''
  })

  const handleGetCustomers = async () => {
    try {
      dispatch(
        showLoading({
          message: 'Carregando',
          isLoading: true
        })
      )

      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      }

      const { data } = await axiosAuth.get(`/gpempresa/${user?.id}`, config)

      setCustomers(data)
    } catch (error: any) {
      if (isUnauthorized(error)) {
        history.push('/unauthorized')
      } else {
        dispatch(
          setGlobalMessage({
            message: `Ocorreu um erro ao buscar os dados: ${error}`,
            type: 'ERROR'
          })
        )
      }
    } finally {
      dispatch(clearLoading())
    }
  }

  const handleFilter = () => {
    setValuesFilter({
      id_syscambio: values.id_syscambio,
      corporate_name: values.corporate_name,
      fantasy_name: values.fantasy_name,
      document: values.document
    })
  }

  const close = () => {
    handleModal()
    setValuesFilter({
      id_syscambio: '',
      corporate_name: '',
      fantasy_name: '',
      document: ''
    })
    setValues({
      id_syscambio: '',
      corporate_name: '',
      fantasy_name: '',
      document: ''
    })
  }

  useEffect(() => {
    if (showModal) {
      handleGetCustomers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal])

  return (
    <Modal
      title='Buscar cliente'
      showModal={showModal}
      backFocus
      close={() => close()}
      width='100%'
      height='100%'
      maxHeight='85vh'
      maxWidth='900px'
    >
      <Container>
        <ItemRowTop>
          <InputNumber
            label='Código'
            maxWidth='150px'
            maxLength={6}
            value={values.id_syscambio}
            changeValue={(value) =>
              setValues({
                ...values,
                id_syscambio: value
              })
            }
            readOnly={
              String(values.corporate_name) !== '' ||
              String(values.fantasy_name) !== '' ||
              String(values.document) !== ''
            }
          />
          <InputText
            label='Nome Fantasia ou Razão Social'
            maxWidth='350px'
            value={values.corporate_name}
            changeValue={(value) =>
              setValues({
                ...values,
                corporate_name: value,
                fantasy_name: value
              })
            }
            readOnly={
              String(values.document) !== '' ||
              String(values.id_syscambio) !== ''
            }
          />
          <InputCNPJCPF
            label='Documento'
            maxWidth='200px'
            value={values.document}
            changeValue={(value) =>
              setValues({
                ...values,
                document: value
              })
            }
            readOnly={
              String(values.corporate_name) !== '' ||
              String(values.fantasy_name) !== '' ||
              String(values.id_syscambio) !== ''
            }
          />
          <Button type='button' onClick={() => handleFilter()}>
            Buscar
          </Button>
        </ItemRowTop>
        <CardGrid>
          <TableGrid>
            <GridTitle>
              <Text fontWeight='bold'>Código</Text>
            </GridTitle>
            <GridTitle>
              <Text fontWeight='bold'>Razão Social</Text>
            </GridTitle>
            <GridTitle>
              <Text fontWeight='bold'>Nome Fantasia</Text>
            </GridTitle>
            <GridTitle>
              <Text fontWeight='bold'>CPF/CNPJ</Text>
            </GridTitle>
            {/*Mudar depois*/}
            {customers
              .filter(
                (e) =>
                  String(e.id_syscambio)
                    ?.toLowerCase()
                    .indexOf(String(valuesFilter.id_syscambio).toLowerCase()) >
                    -1 &&
                  String(e.corporate_name)
                    ?.toLowerCase()
                    .indexOf(
                      String(valuesFilter.corporate_name).toLowerCase()
                    ) > -1 &&
                  String(e.document)
                    ?.toLowerCase()
                    .indexOf(String(valuesFilter.document).toLowerCase()) > -1
              )
              .map((customer, index) => {
                let last = false
                if (index === customers.length - 1) {
                  last = true
                }
                return (
                  <Customer
                    last={last}
                    index={index}
                    customer={customer}
                    handleModal={close}
                    data={data}
                    changeData={changeData}
                  />
                )
              })}
          </TableGrid>
        </CardGrid>
      </Container>
    </Modal>
  )
}

export default FindCustomer
